// features/seller/chat/SellerChats.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getUserChats } from '../../../src/services/chatService';
import { 
  MessageCircle, 
  Clock, 
  Package,
  ArrowRight,
  Users,
  BadgeCheck,
  Filter
} from 'lucide-react';

const SellerChats = () => {
  const { user } = useAuth();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const loadChats = async () => {
      if (!user) return;
      
      try {
        const userChats = await getUserChats(user.uid, 'seller');
        setChats(userChats);
      } catch (error) {
        console.error('Error loading chats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadChats();
  }, [user]);

  const filteredChats = chats.filter(chat => {
    if (filter === 'unread') {
      return chat.unreadCount?.seller > 0;
    }
    if (filter === 'recent') {
      const threeDaysAgo = new Date();
      threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
      return new Date(chat.lastMessageTime) > threeDaysAgo;
    }
    return true;
  });

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffHours < 72) return `${Math.floor(diffHours / 24)}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 bg-gray-200 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  // Calculate metrics
  const totalUnread = chats.reduce((sum, chat) => sum + (chat.unreadCount?.seller || 0), 0);
  const recentChats = chats.filter(chat => {
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
    return new Date(chat.lastMessageTime) > oneDayAgo;
  }).length;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Buyer Inquiries</h1>
            <p className="text-gray-600 mt-1">Manage conversations with potential buyers</p>
          </div>
          <div className="flex items-center space-x-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-600">{totalUnread}</p>
              <p className="text-xs text-gray-500">Unread</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{chats.length}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{recentChats}</p>
              <p className="text-xs text-gray-500">Today</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-xl">
          <div className="flex items-center">
            <div className="h-10 w-10 rounded-full bg-orange-500 flex items-center justify-center mr-3">
              <MessageCircle className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Active Conversations</p>
              <p className="text-2xl font-bold text-gray-900">{chats.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-xl">
          <div className="flex items-center">
            <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center mr-3">
              <Users className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Potential Buyers</p>
              <p className="text-2xl font-bold text-gray-900">
                {new Set(chats.map(chat => chat.buyerId)).size}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-xl">
          <div className="flex items-center">
            <div className="h-10 w-10 rounded-full bg-green-500 flex items-center justify-center mr-3">
              <BadgeCheck className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Response Rate</p>
              <p className="text-2xl font-bold text-gray-900">98%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4 mb-6">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${filter === 'all' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
        >
          All Inquiries
        </button>
        <button
          onClick={() => setFilter('unread')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${filter === 'unread' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
        >
          Unread ({totalUnread})
        </button>
        <button
          onClick={() => setFilter('recent')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${filter === 'recent' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
        >
          Recent (3 days)
        </button>
      </div>

      {filteredChats.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <MessageCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">No buyer inquiries yet</p>
          <p className="text-sm text-gray-500 mt-2">
            {filter !== 'all' 
              ? 'No chats match your filter criteria' 
              : 'Buyers will contact you when interested in your products'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredChats.map((chat) => (
            <Link
              key={chat.id}
              to={`/seller/chats/${chat.id}`}
              className="block bg-white p-4 rounded-xl border hover:border-orange-300 hover:shadow-lg transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                    <Package className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{chat.productName}</h3>
                    <div className="flex items-center space-x-3 mt-1">
                      <div className="flex items-center text-sm text-gray-600">
                        <Users className="h-3 w-3 mr-1" />
                        Buyer: {chat.buyerName}
                      </div>
                      <div className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-full">
                        {chat.buyerEmail}
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 mt-2">
                      Last message: {chat.lastMessage}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <Clock className="h-3 w-3" />
                    <span>{formatTime(chat.lastMessageTime)}</span>
                  </div>
                  {chat.unreadCount?.seller > 0 && (
                    <span className="inline-block mt-3 px-3 py-1 text-sm font-bold bg-orange-600 text-white rounded-full">
                      {chat.unreadCount.seller} unread
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default SellerChats;